// status-renderer-html.ts
//
// Split out of status-renderer.ts (P13 Agent C: grew past the 200-line
// budget once the operating-table columns/summary lines were added — review
// 260909-p6-m2 INFO's "split files >200 lines" applies here too even though
// this specific file wasn't in the original over-200 list, since it crossed
// the line during THIS phase's own work). Owns HTML escaping + rendering +
// the atomic write to `<repo_root>/.avatar/status.html`. All payload
// building (journal read, cost, operating table) stays in status-renderer.ts
// — this module is pure string templating over an already-built payload.
import { join } from "node:path";
import { writeTextAtomic } from "../filesystem-helpers.js";
import { usdCeilingOrUndefined } from "./schemas/config-budget-ceilings.js";
import {
  formatAttemptCell,
  formatPendingGateRequests,
  visibleLastVerdict,
} from "./status-operating-table.js";
import type { StatusPayload, StatusPayloadWithOperatingTable } from "./status-renderer.js";

/** Minimal HTML-escape — no new dependency, covers the five XSS-relevant chars. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Render the status HTML page. ALL untrusted content (mission id, ticket ids,
 * status strings — anything that could contain blueprint-derived text) MUST
 * go through escapeHtml. Never renders secret values — the payload built by
 * buildStatusPayload only carries mission/ticket/status metadata, never
 * config secrets or tokens.
 *
 * Accepts the bare `StatusPayload` shape (existing tests keep using literal
 * objects with no `tickets_detail`/`mission_summary`/`cost` fields) as well
 * as the wider `StatusPayloadWithOperatingTable` — every extra field is read
 * through optional chaining so neither layer is required.
 */
export function renderStatusHtml(
  payload: StatusPayload & Partial<StatusPayloadWithOperatingTable>,
): string {
  const detailByTicket = new Map(payload.tickets_detail?.map((row) => [row.id, row]) ?? []);
  // Row source: `tickets_detail` (when present) is the AUTHORITATIVE full
  // ticket list — it comes from `.avatar/tickets/*.json`, so it includes a
  // ticket that has never been journaled yet (still "todo", no
  // ticket_state_changed event committed). `payload.tickets` alone would
  // silently drop such a ticket from the table. Bare-payload callers (no
  // tickets_detail) keep the original journal-keys-only behavior.
  const ticketIds =
    payload.tickets_detail !== undefined
      ? payload.tickets_detail.map((row) => row.id)
      : Object.keys(payload.tickets);
  const ticketRows = ticketIds
    .map((ticket) => {
      const detail = detailByTicket.get(ticket);
      const status = detail?.status ?? payload.tickets[ticket] ?? "todo";
      const verdict = visibleLastVerdict(status, payload.ticket_last_verdict[ticket]);
      const reasonCell = verdict
        ? `${escapeHtml(verdict.category)}: ${escapeHtml(verdict.reasons.join("; "))}`
        : "";
      const kindCell = detail ? escapeHtml(detail.kind) : "";
      const sliceCell = detail ? escapeHtml(detail.sliceId) : "";
      const attemptCell = detail ? formatAttemptCell(detail) : "";
      return `<tr><td>${escapeHtml(ticket)}</td><td>${kindCell}</td><td>${sliceCell}</td><td>${escapeHtml(status)}</td><td>${escapeHtml(attemptCell)}</td><td>${reasonCell}</td></tr>`;
    })
    .join("\n");

  const summary = payload.mission_summary;
  const summaryLine = summary
    ? `<p>Gate đang chờ: ${escapeHtml(formatPendingGateRequests(summary.pendingGates) || "(không có)")} · quota wake_at: ${escapeHtml(summary.quotaWakeAt ?? "(không sleeping)")}</p>`
    : "";
  // 06g R5: `/ trần $Y` next to the spend when the payload carries limits;
  // 3.38.1: limits without a USD key ⇒ ` (không trần USD)`.
  const usdCeiling = usdCeilingOrUndefined(payload.limits?.max_cost_usd_per_mission);
  const ceiling =
    payload.limits === undefined
      ? ""
      : usdCeiling === undefined
        ? " (không trần USD)"
        : ` / trần $${usdCeiling}`;
  const costLine = payload.cost
    ? `<p>Chi phí: $${escapeHtml(String(payload.cost.total_cost_usd ?? "(chưa có)"))}${escapeHtml(ceiling)} · attempts: ${payload.cost.attempts_total}</p>`
    : "";

  return `<!doctype html>
<html>
<head><meta charset="utf-8"><title>Autopilot status</title></head>
<body>
  <h1>Mission ${escapeHtml(payload.mission_id ?? "(chưa có)")}</h1>
  <p>Status: ${escapeHtml(payload.status ?? "(unknown)")} · Store: ${escapeHtml(payload.store_status)}</p>
  <p>last_event_seq: ${payload.last_event_seq}</p>
  ${summaryLine}
  ${costLine}
  <table>
    <thead><tr><th>Ticket</th><th>Kind</th><th>Slice</th><th>Trạng thái</th><th>Attempt</th><th>Lý do (verdict gần nhất)</th></tr></thead>
    <tbody>${ticketRows}</tbody>
  </table>
</body>
</html>
`;
}

/** Atomic write of the rendered HTML to `<repo_root>/.avatar/status.html`. */
export async function writeStatusHtml(
  repoRoot: string,
  payload: StatusPayload & Partial<StatusPayloadWithOperatingTable>,
): Promise<void> {
  const path = join(repoRoot, ".avatar", "status.html");
  await writeTextAtomic(path, renderStatusHtml(payload));
}
